import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Table, Button, Checkbox, Input, Header, Message, Icon } from 'semantic-ui-react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';

export interface WorkingHoursData {
  id?: string;
  dayOfWeek: number;
  openTime?: string;
  closeTime?: string;
  breakStart?: string;
  breakEnd?: string;
  isClosed: boolean;
}

interface WorkingHoursProps {
  title?: string;
  workingHours: WorkingHoursData[];
  onChange: (workingHours: WorkingHoursData[]) => void;
  parentId: string;
}

const generateId = () => {
  return uuidv4();
};

const WorkingHours: React.FC<WorkingHoursProps> = ({ title, workingHours, onChange, parentId }) => {
  const { t } = useTranslation();
  const [localWorkingHours, setLocalWorkingHours] = useState<WorkingHoursData[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const hasInitialized = useRef(false);
  
  const daysOfWeek = [
    { number: 1, key: 'workingHours.days.monday' },
    { number: 2, key: 'workingHours.days.tuesday' },
    { number: 3, key: 'workingHours.days.wednesday' },
    { number: 4, key: 'workingHours.days.thursday' },
    { number: 5, key: 'workingHours.days.friday' },
    { number: 6, key: 'workingHours.days.saturday' },
    { number: 0, key: 'workingHours.days.sunday' }
  ];
  
  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const initializeWorkingHours = useCallback(() => {
    return daysOfWeek.map(day => {
      const existingHour = workingHours.find(wh => wh.dayOfWeek === day.number);
      return existingHour || {
        id: generateId(),
        dayOfWeek: day.number,
        openTime: '09:00',
        closeTime: '17:00',
        breakStart: '',
        breakEnd: '',
        isClosed: false
      };
    });
  }, [workingHours]);

  useEffect(() => {
    if (!hasInitialized.current) {
      const newWorkingHours = initializeWorkingHours();
      setLocalWorkingHours(newWorkingHours);
      
      setTimeout(() => {
        onChangeRef.current(newWorkingHours);
      }, 0);
      
      hasInitialized.current = true;
    }
  }, [parentId, initializeWorkingHours]);

  useEffect(() => {
    if (hasInitialized.current && workingHours.length > 0) {
      const hasChanges = daysOfWeek.some(day => {
        const existing = localWorkingHours.find(wh => wh.dayOfWeek === day.number);
        const incoming = workingHours.find(wh => wh.dayOfWeek === day.number);
        
        if (!existing && !incoming) return false;
        if (!existing || !incoming) return true;
        
        return (
          existing.openTime !== incoming.openTime ||
          existing.closeTime !== incoming.closeTime ||
          existing.breakStart !== incoming.breakStart ||
          existing.breakEnd !== incoming.breakEnd ||
          existing.isClosed !== incoming.isClosed
        );
      });

      if (hasChanges) {
        const newWorkingHours = initializeWorkingHours();
        setLocalWorkingHours(newWorkingHours);
      }
    }
  }, [workingHours, localWorkingHours, initializeWorkingHours]);

  const notifyParent = useCallback((newHours: WorkingHoursData[]) => {
    const timeoutId = setTimeout(() => {
      onChangeRef.current(newHours);
    }, 10);
    
    return () => clearTimeout(timeoutId);
  }, []);

  const updateWorkingHour = useCallback((dayOfWeek: number, field: keyof WorkingHoursData, value: any) => {
    setLocalWorkingHours(prev => {
      const updated = prev.map(wh => 
        wh.dayOfWeek === dayOfWeek 
          ? { ...wh, [field]: value }
          : wh
      );
      
      notifyParent(updated);
      
      return updated;
    });
  }, [notifyParent]);

  const copyToAll = useCallback((sourceDay: number, field: 'openTime' | 'closeTime' | 'breakStart' | 'breakEnd') => {
    const sourceHour = localWorkingHours.find(wh => wh.dayOfWeek === sourceDay);
    if (!sourceHour) return;

    const value = sourceHour[field];
    setLocalWorkingHours(prev => {
      const updated = prev.map(wh => ({ ...wh, [field]: value }));
      
      notifyParent(updated);
      
      return updated;
    });
  }, [localWorkingHours, notifyParent]);

  const toggleAllClosed = useCallback(() => {
    const allClosed = localWorkingHours.every(wh => wh.isClosed);
    setLocalWorkingHours(prev => {
      const updated = prev.map(wh => ({ ...wh, isClosed: !allClosed }));
      
      notifyParent(updated);
      
      return updated;
    });
  }, [localWorkingHours, notifyParent]);

  const formatTime = (time?: string) => {
    if (!time) return '';
    if (time.length === 5) return time;
    if (time.length === 8) return time.substring(0, 5);
    return time;
  };

  const allClosed = localWorkingHours.every(wh => wh.isClosed);

  // Mobile card view
  const renderMobileView = () => (
    <div style={{ marginTop: '10px' }}>
      {daysOfWeek.map(day => {
        const workingHour = localWorkingHours.find(wh => wh.dayOfWeek === day.number);
        if (!workingHour) return null;

        return (
          <div
            key={day.number}
            style={{
              border: '1px solid #ddd',
              borderRadius: '8px',
              padding: '15px',
              marginBottom: '15px',
              backgroundColor: workingHour.isClosed ? '#f8f8f8' : 'white',
              opacity: workingHour.isClosed ? 0.7 : 1
            }}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '12px',
              paddingBottom: '10px',
              borderBottom: '2px solid #e0e0e0'
            }}>
              <strong style={{ fontSize: '16px' }}>{t(day.key)}</strong>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '13px', color: '#666' }}>
                  {t('workingHours.table.closed')}
                </span>
                <Checkbox
                  checked={workingHour.isClosed}
                  onChange={(_, data) => 
                    updateWorkingHour(day.number, 'isClosed', data.checked)
                  }
                />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '13px', 
                  fontWeight: '600',
                  marginBottom: '5px',
                  color: '#555'
                }}>
                  {t('workingHours.table.openTime')}
                </label>
                <Input
                  type="time"
                  value={formatTime(workingHour.openTime)}
                  onChange={(e) => 
                    updateWorkingHour(day.number, 'openTime', e.target.value)
                  }
                  disabled={workingHour.isClosed}
                  fluid
                />
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '13px', 
                  fontWeight: '600',
                  marginBottom: '5px',
                  color: '#555'
                }}>
                  {t('workingHours.table.closeTime')}
                </label>
                <Input
                  type="time"
                  value={formatTime(workingHour.closeTime)}
                  onChange={(e) => 
                    updateWorkingHour(day.number, 'closeTime', e.target.value)
                  }
                  disabled={workingHour.isClosed}
                  fluid
                />
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '13px', 
                  fontWeight: '600',
                  marginBottom: '5px',
                  color: '#555'
                }}>
                  {t('workingHours.table.breakStart')}
                </label>
                <Input
                  type="time"
                  value={formatTime(workingHour.breakStart)}
                  onChange={(e) => 
                    updateWorkingHour(day.number, 'breakStart', e.target.value)
                  }
                  disabled={workingHour.isClosed}
                  placeholder={t('workingHours.placeholders.optional')}
                  fluid
                />
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '13px', 
                  fontWeight: '600',
                  marginBottom: '5px',
                  color: '#555'
                }}>
                  {t('workingHours.table.breakEnd')}
                </label>
                <Input
                  type="time"
                  value={formatTime(workingHour.breakEnd)}
                  onChange={(e) => 
                    updateWorkingHour(day.number, 'breakEnd', e.target.value)
                  }
                  disabled={workingHour.isClosed}
                  placeholder={t('workingHours.placeholders.optional')}
                  fluid
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  // Desktop table view
  const renderDesktopView = () => (
    <div style={{ overflowX: 'auto' }}>
      <Table celled compact size="small">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={2}>{t('workingHours.table.day')}</Table.HeaderCell>
            <Table.HeaderCell width={1} textAlign="center">{t('workingHours.table.closed')}</Table.HeaderCell>
            <Table.HeaderCell width={3}>
              {t('workingHours.table.openTime')}
              <Button
                size="mini"
                type="button" 
                basic
                icon="copy"
                style={{ marginLeft: '5px', padding: '2px 6px' }}
                title={t('workingHours.buttons.copyTooltip.openTime')}
                onClick={() => copyToAll(1, 'openTime')}
              />
            </Table.HeaderCell>
            <Table.HeaderCell width={3}>
              {t('workingHours.table.closeTime')}
              <Button
                size="mini"
                type="button" 
                basic
                icon="copy"
                style={{ marginLeft: '5px', padding: '2px 6px' }}
                title={t('workingHours.buttons.copyTooltip.closeTime')}
                onClick={() => copyToAll(1, 'closeTime')}
              />
            </Table.HeaderCell>
            <Table.HeaderCell width={3}>
              {t('workingHours.table.breakStart')}
              <Button
                size="mini"
                type="button" 
                basic
                icon="copy"
                style={{ marginLeft: '5px', padding: '2px 6px' }}
                title={t('workingHours.buttons.copyTooltip.breakStart')}
                onClick={() => copyToAll(1, 'breakStart')}
              />
            </Table.HeaderCell>
            <Table.HeaderCell width={3}>
              {t('workingHours.table.breakEnd')}
              <Button
                size="mini"
                type="button" 
                basic
                icon="copy"
                style={{ marginLeft: '5px', padding: '2px 6px' }}
                title={t('workingHours.buttons.copyTooltip.breakEnd')}
                onClick={() => copyToAll(1, 'breakEnd')}
              />
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {daysOfWeek.map(day => {
            const workingHour = localWorkingHours.find(wh => wh.dayOfWeek === day.number);
            if (!workingHour) return null;

            return (
              <Table.Row key={day.number} style={{ 
                backgroundColor: workingHour.isClosed ? '#f8f8f8' : 'transparent',
                opacity: workingHour.isClosed ? 0.6 : 1
              }}>
                <Table.Cell>
                  <strong>{t(day.key)}</strong>
                </Table.Cell>
                <Table.Cell textAlign="center">
                  <Checkbox
                    checked={workingHour.isClosed}
                    onChange={(_, data) => 
                      updateWorkingHour(day.number, 'isClosed', data.checked)
                    }
                  />
                </Table.Cell>
                <Table.Cell>
                  <Input
                    type="time"
                    value={formatTime(workingHour.openTime)}
                    onChange={(e) => 
                      updateWorkingHour(day.number, 'openTime', e.target.value)
                    }
                    disabled={workingHour.isClosed}
                    fluid
                    style={{ 
                      minWidth: '120px',
                      fontSize: '14px'
                    }}
                  />
                </Table.Cell>
                <Table.Cell>
                  <Input
                    type="time"
                    value={formatTime(workingHour.closeTime)}
                    onChange={(e) => 
                      updateWorkingHour(day.number, 'closeTime', e.target.value)
                    }
                    disabled={workingHour.isClosed}
                    fluid
                    style={{ 
                      minWidth: '120px',
                      fontSize: '14px'
                    }}
                  />
                </Table.Cell>
                <Table.Cell>
                  <Input
                    type="time"
                    value={formatTime(workingHour.breakStart)}
                    onChange={(e) => 
                      updateWorkingHour(day.number, 'breakStart', e.target.value)
                    }
                    disabled={workingHour.isClosed}
                    placeholder={t('workingHours.placeholders.optional')}
                    fluid
                    style={{ 
                      minWidth: '120px',
                      fontSize: '14px'
                    }}
                  />
                </Table.Cell>
                <Table.Cell>
                  <Input
                    type="time"
                    value={formatTime(workingHour.breakEnd)}
                    onChange={(e) => 
                      updateWorkingHour(day.number, 'breakEnd', e.target.value)
                    }
                    disabled={workingHour.isClosed}
                    placeholder={t('workingHours.placeholders.optional')}
                    fluid
                    style={{ 
                      minWidth: '120px',
                      fontSize: '14px'
                    }}
                  />
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </div>
  );

  return (
    <div style={{ marginTop: '20px' }}>
      <Header as="h4" dividing>
        <Icon name="clock outline" />
        {title || t('workingHours.title')}
      </Header>
      
      <Message info size="mini">
        <Message.Header>{t('workingHours.setup.header')}</Message.Header>
        <p>{t('workingHours.setup.description')}</p>
        <p><strong>{t('workingHours.setup.tip')}</strong></p>
      </Message>

      <div style={{ marginBottom: '10px' }}>
        <Button
          size="mini"
          type="button" 
          onClick={toggleAllClosed}
          icon={allClosed ? 'eye' : 'eye slash'}
          content={allClosed ? t('workingHours.buttons.openAllDays') : t('workingHours.buttons.closeAllDays')}
        />
        {!isMobile && (
          <span style={{ marginLeft: '10px', fontSize: '12px', color: '#666' }}>
            <Icon name="copy" />
            Use copy buttons to apply values to all days
          </span>
        )}
      </div>

      {isMobile ? renderMobileView() : renderDesktopView()}

      <Message size="mini" style={{ marginTop: '10px' }}>
        <Icon name="info circle" />
        {t('workingHours.setup.breakInfo')}
      </Message>
    </div>
  );
};

export default WorkingHours;